import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import type { User } from '@/types';
import { login as apiLogin, getProfile, verifyToken, LoginCredentials } from '@/lib/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials) => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiLogin(credentials);

          if (response.success && response.data) {
            const { user, token } = response.data;
            localStorage.setItem('token', token);
            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
            });
            return true;
          }

          set({
            error: response.error || 'Login failed',
            isLoading: false,
          });
          return false;
        } catch (error) {
          let message = 'Login failed';
          if (axios.isAxiosError(error)) {
            message = error.response?.data?.error || error.response?.data?.message || error.message;
          } else if (error instanceof Error) {
            message = error.message;
          }
          set({
            error: message,
            isLoading: false,
          });
          return false;
        }
      },

      logout: () => {
        localStorage.removeItem('token');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      checkAuth: async () => {
        const { token } = get();
        if (!token) {
          set({ isAuthenticated: false });
          return;
        }

        try {
          const verifyResponse = await verifyToken();
          if (!verifyResponse.success || !verifyResponse.data?.valid) {
            get().logout();
            return;
          }

          const profileResponse = await getProfile();
          if (profileResponse.success && profileResponse.data) {
            set({
              user: profileResponse.data,
              isAuthenticated: true,
            });
          } else {
            get().logout();
          }
        } catch {
          get().logout();
        }
      },
    }),
    {
      name: 'kurdacars-auth',
      partialize: (state) => ({
        token: state.token,
      }),
    }
  )
);
